/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "delighted",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Why sending surveys should be based on the warehouse",
          "depth": 1,
          "id": "why-sending-surveys-should-be-based-on-the-warehouse"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Delighted",
          "depth": 1,
          "id": "connect-to-delighted"
        }, {
          "value": "Connect to your data source",
          "depth": 1,
          "id": "connect-to-your-data-source"
        }, {
          "value": "Create a model",
          "depth": 1,
          "id": "create-a-model"
        }, {
          "value": "Create a sync",
          "depth": 1,
          "id": "create-a-sync"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Object selection",
          "depth": 1,
          "id": "object-selection"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Custom field mapping",
          "depth": 1,
          "id": "custom-field-mapping"
        }, {
          "value": "Sending surveys",
          "depth": 1,
          "id": "sending-surveys"
        }, {
          "value": "Delete records",
          "depth": 1,
          "id": "delete-records"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      ul: "ul",
      li: "li",
      a: "a",
      strong: "strong",
      ol: "ol",
      img: "img",
      h4: "h4",
      code: "code",
      pre: "pre",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {IntegrationsCount, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!IntegrationsCount) _missingMdxReference("IntegrationsCount", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "The Delighted integration allows you to sync customer data and send surveys from your warehouse based on models that you define."
      }), "\n", _jsx(_components.h3, {
        id: "why-sending-surveys-should-be-based-on-the-warehouse",
        children: "Why sending surveys should be based on the warehouse"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You can do more complex segmentation by leveraging all the data in your warehouse, including event data and third-party data. Your warehouse is the only place where all of this data coexists."
        }), "\n", _jsxs(_components.li, {
          children: ["You can keep your Delighted segments in sync with the rest of your business tools. For example, you might want to send a survey to users who've been invited to a beta program. You also might want to sync these attributes to a CRM like Salesforce or HubSpot. Hightouch integrates with ", _jsx(IntegrationsCount, {}), " SaaS tools used for email marketing, customer support, sales, and more. Define your audience once and activate your data everywhere."]
        }), "\n", _jsxs(_components.li, {
          children: ["Segments can be defined using the tools that your teams are most comfortable with. You can filter and transform data from your warehouse using SQL or our ", _jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "visual query builder"
          }), "—whichever you prefer. (You can even sync customers to or send surveys through Delighted segments based on work you've already done in dbt or Looker!)"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-delighted",
        children: "Connect to Delighted"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Retrieve your API Token from Delighted."
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Delighted."
        }), "\n", _jsxs(_components.li, {
          children: ["Visit ", _jsx(_components.a, {
            href: "https://app.delighted.com/docs/api",
            children: "Delighted's API Introduction Page"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy your ", _jsx(_components.strong, {
            children: "API key"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-delighted-api-key.png",
          alt: "The API key on the Delighted dashboard"
        })
      }), "\n", _jsx(_components.h4, {
        id: "add-delighted-as-a-destination-in-hightouch",
        children: "Add Delighted as a destination in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Hightouch or create a free Hightouch account"
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Destinations"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add destination"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Delighted"
          }), " in the destination catalog and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste your ", _jsx(_components.strong, {
            children: "API key"
          }), " into the ", _jsx(_components.strong, {
            children: "API key"
          }), " field and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a name and unique slug (for example, Delighted Production” and ", _jsx(_components.code, {
            children: "Delighted-production"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your Delighted destination"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-data-source",
        children: "Connect to your data source"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch sits on top of your data warehouse and can read from Snowflake, Redshift, BigQuery, Databricks, Postgres, MySQL, and many other data sources. You can even fetch data from Google Sheets and Airtable. Hightouch can help activate your data wherever it resides."
      }), "\n", _jsx(_components.p, {
        children: "We've written dedicated guides for each supported data source. Take your pick:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/airtable",
            children: "Airtable"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Amazon Athena"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/clickhouse",
            children: "ClickHouse"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/elasticsearch",
            children: "Elasticsearch"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/firebolt",
            children: "Firebolt"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-sheets",
            children: "Google Sheets"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/looker",
            children: "Looker"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/metabase",
            children: "Metabase"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/mysql",
            children: "MySQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/rockset",
            children: "Rockset"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sftp",
            children: "SFTP"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sqlserver",
            children: "SQL Server"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If you're missing the credentials necessary to connect to your data source,\ntry inviting your teammates to Hightouch. You can have unlimited collaborators\nin your workspace."
        })
      }), "\n", _jsx(_components.h4, {
        id: "connect-to-a-sample-database-instead",
        children: "Connect to a sample database instead"
      }), "\n", _jsxs(_components.p, {
        children: ["Waiting for credentials to your data warehouse? No problem. You can connect to one of our ", _jsx(_components.a, {
          href: "/sources/sample-data",
          children: "sample datasets"
        }), " to get started right away. For the rest of this tutorial, we'll use the ", _jsx(_components.strong, {
          children: "B2B SaaS"
        }), " sample source, which contains a ", _jsx(_components.code, {
          children: "users"
        }), " table with names, emails, locations, and other user attributes. This sample source is available when you log in for the first time—no setup required."]
      }), "\n", _jsx(_components.p, {
        children: "Next, we'll write a SQL query to define which users should be synced to our Delighted customers resource. You can follow the same pattern to sync to other support Delighted resources,"
      }), "\n", _jsx(_components.h3, {
        id: "create-a-model",
        children: "Create a model"
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, a model represents a query that filters or transforms the records in your data source. Models are used to determine exactly which records to sync between sources and destinations."
      }), "\n", _jsx(_components.p, {
        children: "When you connect to a data warehouse, your models will probably be written in SQL. You can also import models from tools like dbt and Looker."
      }), "\n", _jsx(_components.p, {
        children: "In this example, we'll keep it simple. Suppose you want to sync all your customers to Delighted. To accomplish this, you'll want to create a model that looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " users;\n"]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Models"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add model"
          }), " in the top right corner."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Demo Database"
          }), " as your source and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "SQL Editor"
          }), " as your modeling method."]
        }), "\n", _jsx(_components.li, {
          children: "Paste the SQL query from above into the editor on the left side of the page."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Preview"
          }), " to execute the query."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your model a name and unique slug (for example, \"All Users\" and ", _jsx(_components.code, {
            children: "all-users"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "ID"
          }), " as the primary key for your model."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your model."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "We just created a model using a SQL query that retrieves all our current users. Next, we'll configure our sync to add these users to Delighted."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync",
        children: "Create a sync"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Syncs"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add sync"
          }), " in the top right corner."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "All Users"
          }), " as your model and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Delighted"
          }), " as your destination and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a name and unique slug (for example, \"Delighted All Customers Sync\" and ", _jsx(_components.code, {
            children: "Delighted-all-customers-sync"
          }), ") and click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "People"
          }), " from the object dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Upsert"
          }), " from the mode radio options."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.code, {
            children: "email"
          }), " and we will map it to ", _jsx(_components.code, {
            children: "Email"
          }), " from the external ID mapping field."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.code, {
            children: "first_name"
          }), " and we will map it to ", _jsx(_components.code, {
            children: "Name"
          }), " in the fields that we'd like to sync to Delighted."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Yes"
          }), " in the Sending Survey section."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Do Nothing"
          }), " in the Delete section."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed to the last step."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Interval"
          }), " to tell your sync to run on a set interval."]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure your sync schedule to run every ", _jsx(_components.strong, {
            children: "5 minutes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your sync."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "And We're done. Your Delighted sync configuration is complete."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "object-selection",
        children: "Object selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Delighted resources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "People"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, records that aren't found in Delighted given the provided identifier will be inserted. And existing records will be updated."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to your Delighted workspace by ", _jsx(_components.code, {
          children: "Email"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported Delighted resources:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Mapping"
            }), _jsx(_components.th, {
              align: "center",
              children: "Type"
            }), _jsx(_components.th, {
              align: "right",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Phone Number"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "String"
            }), _jsx(_components.td, {
              align: "right",
              children: "Phone number of customer. Format must be E.164 (for example, +17132746524)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Survey Channel"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "String"
            }), _jsxs(_components.td, {
              align: "right",
              children: ["Defaults to ", _jsx(_components.code, {
                children: "email"
              }), ". Specify ", _jsx(_components.code, {
                children: "sms"
              }), " and provide a ", _jsx(_components.code, {
                children: "Phone Number"
              }), " to have the survey request sent via SMS."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Name"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "String"
            }), _jsx(_components.td, {
              align: "right",
              children: "Name of person."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Delay To Send Email"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Number"
            }), _jsxs(_components.td, {
              align: "right",
              children: ["Delay in ", _jsx(_components.code, {
                children: "seconds"
              }), " before sending the survey."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Update Email To"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "String"
            }), _jsx(_components.td, {
              align: "right",
              children: "Field to update the person's email."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Update Phone Number To"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "String"
            }), _jsx(_components.td, {
              align: "right",
              children: "Field to update the person's phone number."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "custom-field-mapping",
        children: "Custom field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to set custom attributes. Any mappings from this section will be synced to the custom properties of each associated survey sent. You can add as many properties as you need."
      }), "\n", _jsxs(_components.p, {
        children: ["Properties are a powerful way to analyze survey results on Delighted. To learn more about it, visit ", _jsx(_components.a, {
          href: "https://help.delighted.com/article/575-properties-overview",
          children: "Properties Overview"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sending-surveys",
        children: "Sending surveys"
      }), "\n", _jsx(_components.p, {
        children: "When configuring your Delighted sync form, there are a couple of choices provided to you when it comes to sending surveys. You can either:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Not"
          }), " send surveys to all synced records."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send surveys"
          }), " to all synced records. Records that are created will be sent a survey. Records that are updated and are out of Delighted's survey throttling interval will be sent a survey."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send surveys"
          }), " based on each record ", _jsx(_components.strong, {
            children: "dynamically"
          }), " by providing a ", _jsx(_components.code, {
            children: "Boolean"
          }), " column. To use this feature, toggle ", _jsx(_components.strong, {
            children: "Use Column"
          }), ", and select that respective column from the dropdown."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "delete-records",
        children: "Delete records"
      }), "\n", _jsx(_components.p, {
        children: "When the Hightouch sync engine recognizes that a record has been removed from your source, you can either:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Do Nothing."
        }), "\n", _jsx(_components.li, {
          children: "Delete the record from Delighted."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
